import { ShimmerTwoCol } from "./DashboardShimmer";
import "./dashboard-sections.css";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import { TOTAL_RIPENING_ROOM_CAPACITY, RIPENING_ROOM_CHART_TICK_INTERVAL } from "../../../helpers/constants";
import { useGetColdStorageAndRipeningQuery } from "../dashBoardApi";
import { useEffect } from "react";
import { formatNumber } from "../../../helpers/Utils";
import useTranslator from "../../../hooks/useTranslator";



// ── Dummy data ───────────────────────────────────────────────────────────────
const COLD_DUMMY = {
  total_pallets:         148,
  total_cases:          4320,
  total_fruit_entered: 89600,
};

const RIPENING_DUMMY = {
  total_capacity:      500,
  percentage_occupied: 72,
  total_cases:         360,
  total_weight:      18400,
  rooms: [
    { name: "Room 1", capacity: TOTAL_RIPENING_ROOM_CAPACITY, filled: 180 },
    { name: "Room 2", capacity: TOTAL_RIPENING_ROOM_CAPACITY, filled: 390 },
    { name: "Room 3", capacity: TOTAL_RIPENING_ROOM_CAPACITY, filled: 280 },
    { name: "Room 4", capacity: TOTAL_RIPENING_ROOM_CAPACITY, filled: 168 },
    { name: "Room 5", capacity: TOTAL_RIPENING_ROOM_CAPACITY, filled: 0   },
    { name: "Room 6", capacity: TOTAL_RIPENING_ROOM_CAPACITY, filled: 150 },
    { name: "Room 7", capacity: TOTAL_RIPENING_ROOM_CAPACITY, filled: 260 },
    { name: "Room 8", capacity: TOTAL_RIPENING_ROOM_CAPACITY, filled: 400 },
    { name: "Room 9", capacity: TOTAL_RIPENING_ROOM_CAPACITY, filled: 290 },
  ],
};

// ── Occupancy ring ───────────────────────────────────────────────────────────
const OccupancyRing = ({ pct = 72 }) => {
  const r = 36;
  const circ = 2 * Math.PI * r;
  const filled = (pct / 100) * circ;
  return (
    <div className="db-ring-wrapper">
      <svg width="90" height="90" viewBox="0 0 90 90">
        <circle cx="45" cy="45" r={r} fill="none" stroke="#f0f1f3" strokeWidth="8" />
        <circle
          cx="45" cy="45" r={r}
          fill="none"
          stroke="#ee7225"
          strokeWidth="8"
          strokeDasharray={`${filled} ${circ}`}
          strokeLinecap="round"
          transform="rotate(-90 45 45)"
        />
      </svg>
      <span className="db-ring-label">{pct}%</span>
    </div>
  );
};

// ── Main component ───────────────────────────────────────────────────────────
const ColdRoomDataOverview = ({
  
  coldData     = COLD_DUMMY,
  ripeningData = RIPENING_DUMMY,
  isLoading    = false,
}) => {

  const {
    data : result ,
    isLoading : isResultLoading ,
    isFetching : isResultFetching ,
    isError : errorInResult ,
    refetch : coldStorageRefetch
  } = useGetColdStorageAndRipeningQuery({});

  
  useEffect(() => {
    coldStorageRefetch();
  }, []);
  const { _t } = useTranslator();

  const roomsData = result?.data?.rooms || [];
  const roomAnalytics = result?.data?.room_analytics || [];
  const coldStorageAnalytics = result?.data?.cold_storage_analytics || [];

  const pctNum = ripeningData.percentage_occupied || 0;

  const barOptions = {
    chart: { type: "column", height: 320, backgroundColor: "transparent" },
    title: { text: null },
    credits: { enabled: false },
    xAxis: {
      categories: roomsData?.map((r) => _t(r.name , r.name)),
      labels: { style: { fontSize: "10px", color: "#8c97a7" } },
    },
    yAxis: {
      title: { text: null },
      gridLineColor: "#f0f1f3",
      tickInterval: RIPENING_ROOM_CHART_TICK_INTERVAL,
      max: TOTAL_RIPENING_ROOM_CAPACITY + RIPENING_ROOM_CHART_TICK_INTERVAL,
      labels: { style: { fontSize: "10px", color: "#8c97a7" } },
    },
    legend: {
      align: "center",
      verticalAlign: "top",
      layout: "horizontal",
      itemStyle: { fontSize: "10px", color: "#667085", fontWeight: "500" },
    },
    tooltip: { shared: true },
    plotOptions: {
      column: { grouping: true, borderWidth: 0, borderRadius: 3, dataLabels: { enabled: false } },
    },
    series: [
      {
        name: _t("total_capacity_chart","Total Capacity"),
        data: roomsData?.map((r) => r.cases_capacity),
        color: "#ee7225",
      },
      {
        name: _t("cases_filled","Cases Filled"),
        data: roomsData?.map((r) => r.total_cases),
        color: "#25aa91",
      },
    ],
  };

  return (
    <div className="db-section">
      {isResultLoading ? (
        <ShimmerTwoCol />
      ) : (
        <div className="db-cold-layout">
          {/* ── LEFT: Cold Storage + Ripening Room stacked ── */}
          <div className="db-cold-left">
            <div className="db-avg-card">
              <div className="db-avg-header">
                <span className="db-avg-header-title">{_t("cold_storage_label", "Cold Storage")}</span>
                {/* <span className="db-avg-header-icon">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M12 2v20M2 12h20M4.93 4.93l14.14 14.14M19.07 4.93L4.93 19.07"/>
                  </svg>
                </span> */}
                <span className="pf-live-badge" style={{ fontSize: 10, padding: "3px 9px" }}>
                  <span className="pf-live-dot" />
                  {_t("live_tracking", "Live Tracking")}
                </span>
              </div>

              <div className="db-inner-metric-row">
                <div className="db-inner-metric">
                  <div className="db-metric-label">{_t("total_pallet", "Total Pallet")}</div>
                  <div className="db-metric-value">{formatNumber(coldStorageAnalytics?.total_pallets_in_cold_storage) || 0}</div>
                </div>
                <div className="db-inner-metric">
                  <div className="db-metric-label">{_t("total_cases_live", "Total Cases")}</div>
                  <div className="db-metric-value">{formatNumber(coldStorageAnalytics?.total_cases_in_cold_storage) || 0}</div>
                </div>
                <div className="db-inner-metric">
                  <div className="db-metric-label">{_t("fruit_entered", "Fruit Entered")}</div>
                  <div className="db-metric-value">
                    {formatNumber(coldStorageAnalytics?.total_fruit_kg_in_cold_storage) || 0}
                    <span className="db-metric-unit"> kg</span>
                  </div>
                </div>
              </div>
            </div>

            <div className="db-avg-card">
              <div className="db-avg-header">
                <span className="db-avg-header-title">{_t("ripening_room_label","Ripening Room")}</span>
                <span className="pf-live-badge" style={{ fontSize: 10, padding: "3px 9px" }}>
                  <span className="pf-live-dot" />
                  {_t("live_tracking", "Live Tracking")}
                </span>
              </div>
              <div className="db-ripening-body">
                <div className="db-ripening-ring-col">
                  <OccupancyRing pct={roomAnalytics?.occupied_prcnt} />
                  <span className="db-ring-sublabel">{_t("occupied","Occupied")}</span>
                </div>
                <div className="db-ripening-stats-col">
                  <div className="db-avg-row">
                    <span>{_t("total_capacity_label","Total Capacity")}</span>
                    <span className="db-avg-val">{roomAnalytics?.total_cases_capacity?.toLocaleString()} <span className="db-metric-unit">{_t("cases","cases")}</span></span>
                  </div>
                  <div className="db-avg-row">
                    <span>{_t("total_cases_ripening_label","Total Cases")}</span>
                    <span className="db-avg-val">{roomAnalytics?.total_cases_in_rooms?.toLocaleString()} <span className="db-metric-unit">{_t("cases","cases")}</span></span>
                  </div>
                  <div className="db-avg-row">
                    <span>{_t("total_weight_label","Total Weight")}</span>
                    <span className="db-avg-val">{roomAnalytics?.total_weight_in_rooms?.toLocaleString()} <span className="db-metric-unit">kg</span></span>
                  </div>
                  <div className="db-avg-row">
                    <span>{_t("pct_occupied","% Occupied")}</span>
                    <span className="db-avg-val db-highlight">{roomAnalytics?.occupied_prcnt}%</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* ── RIGHT: Chart filling full height ── */}
          <div className="db-avg-card db-cold-right">
            <div className="db-avg-header">
              <span className="db-avg-header-title">{_t("ripening_room_chart","Ripening Room Chart")}</span>
                <span className="pf-live-badge" style={{ fontSize: 10, padding: "3px 9px" }}>
                  <span className="pf-live-dot" />
                  {_t("live_tracking", "Live Tracking")}
                </span>
            </div>
            <div className="db-cold-chart-body">
              <HighchartsReact highcharts={Highcharts} options={barOptions} />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ColdRoomDataOverview;
